import * as React from 'react';

import { MediaResourceTypes } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import cx from 'classnames';
import ReactPlayer from 'react-player/lazy';
import { useDispatch } from 'react-redux';

import { IconPencil, IconX } from '~shared/components/icons/micro';
import { MediaPlayer } from '~shared/components/MediaPlayer';
import { useEmbedVideoModal } from '~teacher/components/QuizQuestion/EmbedVideoDialog/context/EmbedVideoDialogProvider';
import { VideoResource } from '~teacher/models/quizzes';
import { updateQuestionVideoData } from '~teacher/slices/quiz/quizSlice';

interface Props {
  questionId: string;
  videoData: VideoResource;
  isEditing: boolean;
}

export function QuestionVideoPlayer({
  questionId,
  videoData,
  isEditing,
}: Props) {
  const dispatch = useDispatch();
  const embedVideoModal = useEmbedVideoModal();
  const reactPlayerRef = React.useRef<ReactPlayer>(null);

  const containerClass = cx('rounded-3 p-4', { 'bg-white': isEditing });

  return (
    <div className={containerClass}>
      <div className="relative ">
        {isEditing ? (
          <div className="flex flex-col absolute top-0 right-0 pointer-events-auto m-2 gap-2">
            <button
              onClick={() => {
                dispatch(updateQuestionVideoData({ url: null }));
              }}
              aria-label={translate('Remove {0}').format(
                reactPlayerRef.current?.getInternalPlayer()?.playerInfo
                  ?.videoData?.title ?? ''
              )}
              className="w-10 h-10 bg-grey-50 text-grey-900 hover:text-red-700 focus:text-red-700 focus:shadow-outline border-0 rounded-1 outline-none cursor-pointer appearance-none px-2 sm:py-0"
            >
              <div className="flex relative items-center justify-center">
                <IconX />
              </div>
            </button>
            <button
              onClick={embedVideoModal.open}
              aria-label={translate('Edit {0}').format(
                reactPlayerRef.current?.getInternalPlayer()?.playerInfo
                  ?.videoData?.title ?? ''
              )}
              className="w-10 h-10 bg-grey-50 text-grey-900 hover:text-cyan-700 focus:text-cyan-700 focus:shadow-outline border-0 rounded-1 outline-none cursor-pointer appearance-none px-2 sm:py-0"
            >
              <div className="flex relative items-center justify-center">
                <IconPencil />
              </div>
            </button>
          </div>
        ) : null}

        <div className={isEditing ? 'w-117 h-63' : 'w-100 h-70 sm:w-124'}>
          <MediaPlayer
            key={`question-video-${questionId}`}
            ref={reactPlayerRef}
            url={videoData.url}
            start={videoData?.start}
            end={videoData?.end}
            type={MediaResourceTypes.Video}
            controls={true}
            containerStyle="w-full h-full"
          />
        </div>
      </div>
    </div>
  );
}
